import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    CheckBox,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
    SelectMultiple,
    Text,
    TextInput,
} from 'grommet';
import { API } from '../../api';
import { Utils } from '../../helpers/Utils';
import AppConstants from '../../helpers/AppConstants';
import { RowsPerPageSelect } from '../../components/RowsPerPageSelect';
import {
    resolveSortField,
    usePaginatedSort,
} from '../../hooks/usePaginatedSort';
import { jobStatColumns } from './jobStatColumns';
import QueryString from 'qs';
import { useDebouncedCallback } from 'use-debounce';
import { useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

const SEARCH_DEBOUNCE_MS = 400;

export const JobsDashboard = ({
    companySlug: companySlugProp,
    showAdminActions = true,
    showCreateJobAction,
    showClosedJobsAction = true,
    createJobPath = '/jobs/create',
    showCompanyColumn,
    showClientCompaniesFilter,
    initialStatusFilter,
    showSearch = false,
}) => {
    const showCreateJobActionResolved = showCreateJobAction ?? showAdminActions;
    const STATUS_OPTIONS = AppConstants.jobStatusOptions;
    const STATUS_FILTER_OPTIONS = AppConstants.jobStatusFilterOptions;
    const location = useLocation();
    const companySlug = companySlugProp ?? location.state?.companySlug;
    const showCompanyColumnResolved = showCompanyColumn ?? !companySlug;
    const showClientCompaniesFilterResolved =
        showClientCompaniesFilter ?? !companySlug;
    const [jobs, setJobs] = useState([]);
    const [statusFilter, setStatusFilter] = useState(
        initialStatusFilter ?? location.state?.statusFilter ?? STATUS_OPTIONS,
    );
    const {
        page,
        pageSize,
        sort,
        totalItems,
        stepOptions,
        setPage,
        handleSort,
        handlePageChange,
        handlePageSizeChange,
        applyPagination,
    } = usePaginatedSort({
        initialSort: { property: 'updatedAt', direction: 'desc' },
    });
    const [clientsOnly, setClientsOnly] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
    const navigate = useNavigate();

    // The input stays instant; only the debounced value drives a refetch.
    // A new term also drops back to page 1, since the current offset may not
    // exist in the narrowed set.
    const applySearchTerm = useDebouncedCallback((value) => {
        setAppliedSearchTerm(value);
        setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    const handleStatusFilterChange = ({ value }) => {
        setStatusFilter(value);
        setPage(1);
    };

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const jobResponse = await loadJobs({
                page,
                pageSize,
                statusFilter,
                companySlug,
                sort,
                clientsOnly,
                search: appliedSearchTerm,
            });

            if (isActive && jobResponse?.meta?.pagination) {
                setJobs(Utils.deepFlatten(jobResponse.data));
                applyPagination(jobResponse.meta);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [
        page,
        pageSize,
        statusFilter,
        companySlug,
        sort,
        clientsOnly,
        appliedSearchTerm,
        applyPagination,
    ]);

    const dataColumns = [
        {
            header: 'Job Title',
            property: 'title',
            render: (job) => {
                return (
                    <Anchor
                        key={job.id}
                        href={
                            companySlugProp
                                ? `/${companySlugProp}/jobs/${job.id}`
                                : `/jobs/${job.id}`
                        }
                        label={job.title}
                    />
                );
            },
            sortable: true,
        },
        ...(showCompanyColumnResolved
            ? [
                {
                    header: 'Company',
                    property: 'company.name',
                    render: (job) => {
                        if (!job.company) return 'N/A';
                        return (
                            <Anchor
                                key={job.company.id}
                                href={`/companies/${job.company.id}`}
                                label={job.company.name}
                            />
                        );
                    },
                    sortable: true,
                },
            ]
            : []),
        ...jobStatColumns({ sortable: true }),
    ];

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent>
                <Box gap="small">
                    <Box
                        direction="row-responsive"
                        justify="between"
                        pad={{ right: 'small' }}>
                        <Heading level="2">Jobs</Heading>
                        <Box
                            align="center"
                            justify="center"
                            direction="row-responsive"
                            gap="small">
                            {showClosedJobsAction && (
                                <Button
                                    secondary
                                    size="large"
                                    label="View closed jobs"
                                    onClick={() =>
                                        navigate(
                                            companySlugProp
                                                ? `/${companySlugProp}/jobs/closed`
                                                : '/jobs/closed',
                                        )
                                    }
                                />
                            )}
                            {showCreateJobActionResolved && (
                                <Button
                                    primary
                                    size="large"
                                    label="Create New Job"
                                    onClick={() => navigate(createJobPath)}
                                />
                            )}
                        </Box>
                    </Box>
                    <Box direction="row" align="center" gap="medium" wrap>
                        {showSearch && (
                            <Box width="medium">
                                <TextInput
                                    a11yTitle="Search jobs"
                                    placeholder="Search by job or company name"
                                    value={searchTerm}
                                    onChange={(event) => {
                                        setSearchTerm(event.target.value);
                                        applySearchTerm(event.target.value);
                                    }}
                                />
                            </Box>
                        )}
                        <Box width="medium">
                            <SelectMultiple
                                options={STATUS_FILTER_OPTIONS}
                                labelKey="label"
                                valueKey={{ key: 'value', reduce: true }}
                                showSelectedInline={true}
                                onChange={handleStatusFilterChange}
                                value={statusFilter}
                            />
                        </Box>
                        {showClientCompaniesFilterResolved && (
                            <CheckBox
                                label="Client companies only"
                                checked={clientsOnly}
                                onChange={({ target }) => {
                                    setClientsOnly(target.checked);
                                    setPage(1);
                                }}
                                toggle
                            />
                        )}
                    </Box>
                    <Box>
                        {jobs.length === 0 ? (
                            <Box pad="medium" align="center">
                                <Text>No jobs found</Text>
                            </Box>
                        ) : (
                            <DataTable
                                primaryKey="id"
                                sort={sort}
                                onSort={handleSort}
                                data={jobs}
                                columns={dataColumns}
                            />
                        )}
                    </Box>
                    <Box
                        direction="row-responsive"
                        align="center"
                        justify="between"
                        gap="small"
                        pad="small">
                        <RowsPerPageSelect
                            options={stepOptions}
                            value={pageSize}
                            onChange={handlePageSizeChange}
                        />
                        <Pagination
                            numberItems={totalItems}
                            page={page}
                            onChange={handlePageChange}
                            step={pageSize}
                            summary={true}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

JobsDashboard.propTypes = {
    companySlug: PropTypes.string,
    showAdminActions: PropTypes.bool,
    showCreateJobAction: PropTypes.bool,
    showClosedJobsAction: PropTypes.bool,
    createJobPath: PropTypes.string,
    showCompanyColumn: PropTypes.bool,
    showClientCompaniesFilter: PropTypes.bool,
    initialStatusFilter: PropTypes.arrayOf(PropTypes.string),
    showSearch: PropTypes.bool,
};

const ALL_STATUSES = ['demo', 'preparing', 'open'];

const SORT_FIELD_MAP = {
    title: 'title',
    'company.name': 'company.name',
    seats: 'seats',
    updatedAt: 'updatedAt',
    matchCount: 'matchCount',
    matchSeatRatio: 'matchSeatRatio',
    interestedCount: 'interestedCount',
    appliedCount: 'appliedCount',
};

const loadJobs = async ({
    page,
    pageSize,
    statusFilter = ALL_STATUSES,
    companySlug,
    sort,
    clientsOnly = false,
    search,
} = {}) => {
    const filters = {
        status: {
            $in: statusFilter.length === 0 ? ALL_STATUSES : statusFilter,
        },
    };

    if (companySlug) {
        filters.company = {
            slug: {
                $eqi: companySlug,
            },
        };
    }

    const term = search?.trim();

    // Top-level filter keys are ANDed by Strapi, so the $or narrows the
    // company/status filters rather than widening past them.
    if (term) {
        filters.$or = [
            { title: { $containsi: term } },
            { company: { name: { $containsi: term } } },
        ];
    }

    if (clientsOnly) {
        filters.company = {
            ...filters.company,
            isContractClient: {
                $eq: true,
            },
        };
    }

    const query = QueryString.stringify(
        {
            filters,
            populate: {
                company: {
                    fields: ['id', 'name', 'slug'],
                },
            },
            pagination: page && pageSize ? { page, pageSize } : undefined,
            sort: [
                `${resolveSortField(sort, SORT_FIELD_MAP, 'updatedAt')}:${
                    sort?.direction ?? 'desc'
                }`,
            ],
        },
        { encodeValuesOnly: true },
    );

    try {
        const response = await API.get(`/api/jobs?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return [];
};
